import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import cola from 'cytoscape-cola';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Popover,
  Button,
  Divider,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  OutlinedInput,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  Fullscreen,
  Image as ImageIcon,
} from '@mui/icons-material';
import { companiesAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

// Register layout
cytoscape.use(cola);

/**
 * Darken a hex color by a given factor
 * @param hex - Hex color code (e.g., '#FF0000')
 * @param factor - Darkening factor (0-1, where 1 is black)
 * @returns Darkened hex color
 */
function darkenColor(hex: string, factor: number): string {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Darken by factor
  const darkenedR = Math.round(r * (1 - factor));
  const darkenedG = Math.round(g * (1 - factor));
  const darkenedB = Math.round(b * (1 - factor));

  // Convert back to hex
  return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
}

interface GraphNode {
  id: string;
  type: 'company' | 'person';
  label: string;
  jurisdiction?: string;
  color?: string;
  data?: any;
}

interface GraphEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  data?: any;
}

interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
  stats: {
    totalNodes: number;
    totalEdges: number;
    companies: number;
    persons: number;
  };
}

interface OwnershipGraphProps {
  companyId?: string;
  jurisdiction?: string;
  height?: string | number;
}

const OwnershipGraph: React.FC<OwnershipGraphProps> = ({
  companyId,
  jurisdiction,
  height = '800px',
}) => {
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [selectedEdge, setSelectedEdge] = useState<any>(null);
  const cyRef = useRef<any>(null);
  const layoutDoneRef = useRef<boolean>(false);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const graphHeight = isMobile ? 'calc(100vh - 320px)' : height;
  const [search, setSearch] = useState('');
  const [showCompanies, setShowCompanies] = useState(true);
  const [showPersons, setShowPersons] = useState(true);
  const [hideNonMatches, setHideNonMatches] = useState(false);
  const [kHops] = useState<number>(0);
  const [minDegree] = useState<number>(0);
  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>([]);
  const [matchResults, setMatchResults] = useState<any[]>([]);
  const [graphId, setGraphId] = useState<number>(0);

  const fetchGraphData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await companiesAPI.getOwnershipGraph(companyId, jurisdiction);
      setGraphData(response.data);
      setGraphId(prev => prev + 1); // Increment to force new key
      layoutDoneRef.current = false; // Reset layout flag on new data
      // clear previous results
      setMatchResults([]);
      // initialize jurisdictions from data (once per load)
      try {
        const js = new Set<string>();
        (response.data?.nodes || []).forEach((n: any) => {
          if (n.type === 'company' && n.jurisdiction) js.add(String(n.jurisdiction));
        });
        setJurisdictions(Array.from(js).sort());
      } catch {}
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load ownership graph';
      setError(errorMessage);
      console.error('Error fetching graph data:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, jurisdiction]);

  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  // Track significant filter changes to force layout recalculation
  const filterSignature = useMemo(() => {
    return `${showCompanies}-${showPersons}-${selectedJurisdictions.join(',')}-${hideNonMatches}-${minDegree}-${kHops}`;
  }, [showCompanies, showPersons, selectedJurisdictions, hideNonMatches, minDegree, kHops]);

  useEffect(() => {
    // Increment graphId when filters change to force CytoscapeComponent remount
    if (graphData) {
      setGraphId(prev => prev + 1);
    }
  }, [filterSignature, graphData]);

  const handleZoomIn = () => {
    if (cyRef.current) {
      const cy = cyRef.current;
      cy.zoom(cy.zoom() * 1.2);
    }
  };

  const handleZoomOut = () => {
    if (cyRef.current) {
      const cy = cyRef.current;
      cy.zoom(cy.zoom() * 0.8);
    }
  };

  const handleCenter = () => {
    if (cyRef.current) {
      cyRef.current.fit();
    }
  };

  const handleFullscreen = () => {
    const element = document.getElementById('graph-container');
    if (element) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        element.requestFullscreen();
      }
    }
  };

  const handleExportPng = () => {
    const cy = cyRef.current;
    if (!cy) return;
    try {
      const blob = cy.png({ output: 'blob', full: true });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = companyId ? `ownership-graph-${companyId}.png` : 'ownership-graph.png';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export PNG failed:', e);
    }
  };

  // Transform data for Cytoscape - memoize to prevent re-creation on state changes
  // IMPORTANT: All hooks must be called before any conditional returns
  const elements = useMemo(() => {
    if (!graphData) return [];

    let filteredNodes = [...graphData.nodes];
    let filteredEdges = [...graphData.edges];

    // 1. Filter by type (companies/persons)
    if (!showCompanies) {
      filteredNodes = filteredNodes.filter(n => n.type !== 'company');
    }
    if (!showPersons) {
      filteredNodes = filteredNodes.filter(n => n.type !== 'person');
    }

    // 2. Filter by jurisdiction
    if (selectedJurisdictions.length > 0) {
      filteredNodes = filteredNodes.filter(n =>
        n.type === 'person' ||
        (n.jurisdiction && selectedJurisdictions.includes(n.jurisdiction))
      );
    }

    // 3. Filter by search
    let searchMatches: Set<string> = new Set();
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filteredNodes.forEach(n => {
        if (n.label.toLowerCase().includes(searchLower) ||
            (n.jurisdiction && n.jurisdiction.toLowerCase().includes(searchLower))) {
          searchMatches.add(n.id);
        }
      });

      if (hideNonMatches && searchMatches.size > 0) {
        filteredNodes = filteredNodes.filter(n => searchMatches.has(n.id));
      }
    }

    // 4. Filter by minimum degree (connection count)
    if (minDegree > 0) {
      const degreeMap = new Map<string, number>();
      graphData.edges.forEach(edge => {
        degreeMap.set(edge.source, (degreeMap.get(edge.source) || 0) + 1);
        degreeMap.set(edge.target, (degreeMap.get(edge.target) || 0) + 1);
      });
      filteredNodes = filteredNodes.filter(n => (degreeMap.get(n.id) || 0) >= minDegree);
    }

    // 5. Filter by k-hop neighborhood (if kHops > 0 and we have search matches)
    if (kHops > 0 && searchMatches.size > 0) {
      const neighborhood = new Set<string>(searchMatches);

      // Expand neighborhood k times
      for (let hop = 0; hop < kHops; hop++) {
        const currentNeighbors = new Set(neighborhood);
        graphData.edges.forEach(edge => {
          if (currentNeighbors.has(edge.source)) {
            neighborhood.add(edge.target);
          }
          if (currentNeighbors.has(edge.target)) {
            neighborhood.add(edge.source);
          }
        });
      }

      filteredNodes = filteredNodes.filter(n => neighborhood.has(n.id));
    }

    // Create a set of valid node IDs for edge filtering
    const validNodeIds = new Set(filteredNodes.map(n => n.id));

    // Filter edges to only include those connecting visible nodes
    filteredEdges = filteredEdges.filter(e =>
      validNodeIds.has(e.source) && validNodeIds.has(e.target)
    );

    // Update match results for search result chips
    if (search.trim() && searchMatches.size > 0) {
      const matches = filteredNodes.filter(n => searchMatches.has(n.id));
      if (JSON.stringify(matches) !== JSON.stringify(matchResults)) {
        setMatchResults(matches);
      }
    } else if (matchResults.length > 0) {
      setMatchResults([]);
    }

    // Group company nodes by jurisdiction for clustering
    const jurisdictionGroups = new Map<string, GraphNode[]>();
    const personNodes: GraphNode[] = [];

    filteredNodes.forEach(node => {
      if (node.type === 'person') {
        personNodes.push(node);
      } else {
        const jurisdiction = node.jurisdiction || 'Unknown';
        if (!jurisdictionGroups.has(jurisdiction)) {
          jurisdictionGroups.set(jurisdiction, []);
        }
        jurisdictionGroups.get(jurisdiction)!.push(node);
      }
    });

    const elements: any[] = [];

    // Add person nodes (not clustered)
    personNodes.forEach(node => {
      // Generate photo URL if profilePhotoUrl exists
      const photoUrl = node.data?.profilePhotoUrl && node.id
        ? `${window.location.protocol}//${window.location.host}/api/persons/${node.id.replace('person-', '')}/profile-photo`
        : undefined;
      
      elements.push({
        data: {
          id: node.id,
          label: node.label,
          type: node.type,
          photoUrl,
          ...node.data,
        },
        classes: [
          (searchMatches.has(node.id) && search.trim()) ? 'search-match' : '',
        ].filter(Boolean).join(' '),
      });
    });

    // Add jurisdiction parent nodes and company child nodes
    jurisdictionGroups.forEach((nodes, jurisdiction) => {
      const parentId = `jurisdiction-${jurisdiction}`;

      // Add parent node for jurisdiction
      elements.push({
        data: {
          id: parentId,
          label: jurisdiction,
          type: 'jurisdiction-group',
        },
        classes: 'jurisdiction-parent',
      });

      // Add company nodes as children of jurisdiction parent
      nodes.forEach(node => {
        elements.push({
          data: {
            id: node.id,
            parent: parentId, // Set parent for clustering
            label: node.label,
            type: node.type,
            jurisdiction: node.jurisdiction,
            color: node.color,
            ...node.data,
          },
          classes: [
            (searchMatches.has(node.id) && search.trim()) ? 'search-match' : '',
            (String((node as any)?.data?.status || '').toUpperCase() === 'WOUND_UP' || /wind\s*up|winding/i.test(String((node as any)?.data?.comments || ''))) ? 'windup' : '',
          ].filter(Boolean).join(' '),
        });
      });
    });

    // Add edges
    elements.push(
      ...filteredEdges.map((edge) => {
        // Extract percentage from label if it exists
        const percentMatch = edge.label?.match(/(\d+(?:\.\d+)?)\s*%/);
        const percentage = percentMatch ? parseFloat(percentMatch[1]) : null;

        // Classify ownership level
        let ownershipLevel = 'unknown';
        if (percentage !== null) {
          if (percentage >= 50) ownershipLevel = 'majority';
          else if (percentage >= 25) ownershipLevel = 'significant';
          else if (percentage >= 10) ownershipLevel = 'moderate';
          else ownershipLevel = 'minor';
        }

        return {
          data: {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            label: edge.label,
            percentage,
            ownershipLevel,
            ...edge.data,
          },
          classes: ownershipLevel,
        };
      }),
    );

    return elements;
  }, [graphData, showCompanies, showPersons, selectedJurisdictions, search, hideNonMatches, minDegree, kHops, matchResults]);

  // Responsive node sizing for mobile
  const nodeSize = isMobile ? '48px' : '60px';
  const nodeFontSize = isMobile ? '10px' : '12px';
  const nodeTextMaxWidth = isMobile ? '100px' : '150px';

  const stylesheet: any[] = [
    {
      selector: 'node',
      style: {
        'background-color': '#4CAF50',
        label: 'data(label)',
        'text-wrap': 'wrap',
        'text-max-width': nodeTextMaxWidth,
        'font-size': nodeFontSize,
        'font-weight': '600',
        'text-valign': 'center',
        'text-halign': 'center',
        color: '#ffffff',
        'text-outline-color': '#000000',
        'text-outline-width': 2,
        'text-background-color': 'rgba(0, 0, 0, 0.75)',
        'text-background-opacity': 1,
        'text-background-padding': '4px',
        'text-background-shape': 'roundrectangle',
        'border-width': 2,
        'border-color': '#2E7D32',
        width: nodeSize,
        height: nodeSize,
        'overlay-opacity': 0,
        'cursor': 'pointer',
      },
    },
    {
      selector: 'node.jurisdiction-parent',
      style: {
        'background-color': '#f5f5f5',
        'background-opacity': 0.3,
        'border-width': 2,
        'border-color': '#999',
        'border-style': 'dashed',
        'text-valign': 'top',
        'text-halign': 'center',
        'text-margin-y': -10,
        'font-size': '14px',
        'font-weight': 'bold',
        color: '#333',
        'text-outline-width': 0,
        'text-background-opacity': 0,
        'padding': '20px',
        shape: 'roundrectangle',
      },
    },
    {
      selector: 'node[type="company"]',
      style: {
        'background-color': 'data(color)',
        'border-color': (ele: any) => {
          const color = ele.data('color');
          // Darken the border color by reducing brightness
          return color ? darkenColor(color, 0.3) : '#1565C0';
        },
        shape: 'rectangle',
      },
    },
    {
      selector: 'node.windup',
      style: {
        'border-width': 4,
        'border-color': '#B71C1C',
        'background-color': (ele: any) => {
          const base = ele.data('color') || '#EF9A9A';
          return base;
        },
      },
    },
    {
      selector: 'node[type="person"]',
      style: {
        'background-color': '#FF9800',
        'border-color': '#E65100',
        shape: 'ellipse',
      },
    },
    // Person node with profile photo
    {
      selector: 'node[type="person"][photoUrl]',
      style: {
        'background-image': 'data(photoUrl)',
        'background-fit': 'cover',
        'background-clip': 'node',
        'border-width': 3,
        'border-color': '#FF9800',
        shape: 'ellipse',
      },
    },
    {
      selector: 'node:selected',
      style: {
        'border-width': 4,
        'border-color': '#F44336',
      },
    },
    {
      selector: 'node.search-match',
      style: {
        'border-width': 3,
        'border-color': '#FFD700',
        'border-style': 'solid',
      },
    },
    {
      selector: 'node.dimmed',
      style: {
        opacity: 0.3,
      },
    },
    {
      selector: 'edge',
      style: {
        width: 2,
        'line-color': '#999',
        'target-arrow-color': '#999',
        'target-arrow-shape': 'triangle',
        'curve-style': 'bezier',
        label: 'data(label)',
        'font-size': '12px',
        'font-weight': 'bold',
        color: '#1976d2',
        'text-rotation': 'autorotate',
        'text-background-color': '#fff',
        'text-background-opacity': 0.95,
        'text-background-padding': '4px',
        'text-background-shape': 'roundrectangle',
        'text-border-color': '#1976d2',
        'text-border-width': 1,
        'text-border-opacity': 0.5,
        'min-zoomed-font-size': 8,
      },
    },
    // Majority ownership (≥50%) - Red, thick
    {
      selector: 'edge.majority',
      style: {
        width: 4,
        'line-color': '#d32f2f',
        'target-arrow-color': '#d32f2f',
        color: '#d32f2f',
        'text-border-color': '#d32f2f',
        'font-size': '14px',
      },
    },
    // Significant ownership (25-49%) - Orange, medium-thick
    {
      selector: 'edge.significant',
      style: {
        width: 3,
        'line-color': '#f57c00',
        'target-arrow-color': '#f57c00',
        color: '#f57c00',
        'text-border-color': '#f57c00',
        'font-size': '13px',
      },
    },
    // Moderate ownership (10-24%) - Blue, medium
    {
      selector: 'edge.moderate',
      style: {
        width: 2.5,
        'line-color': '#1976d2',
        'target-arrow-color': '#1976d2',
        color: '#1976d2',
        'text-border-color': '#1976d2',
        'font-size': '12px',
      },
    },
    // Minor ownership (<10%) - Green, thin
    {
      selector: 'edge.minor',
      style: {
        width: 2,
        'line-color': '#388e3c',
        'target-arrow-color': '#388e3c',
        color: '#388e3c',
        'text-border-color': '#388e3c',
        'font-size': '11px',
      },
    },
    // Unknown ownership (no percentage) - Gray, dashed
    {
      selector: 'edge.unknown',
      style: {
        width: 1.5,
        'line-color': '#9e9e9e',
        'target-arrow-color': '#9e9e9e',
        'line-style': 'dashed',
        color: '#757575',
        'text-border-color': '#9e9e9e',
        'font-size': '10px',
        opacity: 0.7,
      },
    },
  ];

  // Extract filtered node IDs from elements
  const filteredNodeIds = useMemo(() => {
    return new Set(elements.filter(e => e.data.type).map(e => e.data.id));
  }, [elements]);

  // Group nodes by jurisdiction for positioning - based on FILTERED nodes
  const jurisdictionGroups = useMemo(() => {
    if (!graphData) return {};
    const groups: Record<string, string[]> = {};
    graphData.nodes.forEach((node) => {
      // Only include nodes that are in the filtered elements
      if (filteredNodeIds.has(node.id) && node.type === 'company' && node.jurisdiction) {
        if (!groups[node.jurisdiction]) {
          groups[node.jurisdiction] = [];
        }
        groups[node.jurisdiction].push(node.id);
      }
    });
    return groups;
  }, [graphData, filteredNodeIds]);

  // Create alignment constraints for jurisdiction clusters - memoize
  const alignmentConstraints = useMemo(() => {
    const constraints: any[] = [];
    Object.entries(jurisdictionGroups).forEach(([jurisdiction, nodeIds]) => {
      if (nodeIds.length > 1) {
        // Create strong horizontal alignment constraints for nodes in same jurisdiction
        // This keeps all companies from the same jurisdiction in a tight vertical column
        constraints.push({
          type: 'alignment',
          axis: 'x',
          offsets: nodeIds.map((id) => ({ node: id, offset: 0 })),
        });

        // Add vertical spacing constraints to keep nodes within same jurisdiction close
        // Create pairs of nodes for tighter vertical clustering
        for (let i = 0; i < nodeIds.length - 1; i++) {
          constraints.push({
            axis: 'y',
            left: nodeIds[i],
            right: nodeIds[i + 1],
            gap: 80, // Tighter vertical gap between nodes in same jurisdiction
            equality: false,
          });
        }
      }
    });
    return constraints;
  }, [jurisdictionGroups]);

  // Memoize layout config to prevent re-running layout on state changes
  const layout = useMemo(() => ({
    name: 'cola',
    animate: true,
    randomize: false,
    maxSimulationTime: 4000,
    fit: true,
    padding: 30,
    nodeSpacing: 50, // Reduced from 100 - tighter spacing within jurisdictions
    edgeLength: 150, // Reduced from 200 - shorter edges for tighter clusters
    // Group companies by jurisdiction
    alignment: alignmentConstraints.length > 0 ? alignmentConstraints : undefined,
    // Add stronger flow direction to separate jurisdictions
    flow: { axis: 'y', minSeparation: 100 }, // Reduced from 150 - bring jurisdiction groups closer
    // Increase avoidance to keep jurisdiction groups distinct
    avoidOverlap: true,
    handleDisconnected: true,
    unconstrIter: 150, // Increased from 100 - more iterations for tighter convergence
    userConstIter: 100, // Increased from 50 - stronger constraint enforcement
    allConstIter: 150, // Increased from 100 - overall tighter layout
    // Add convergence threshold for tighter packing
    convergenceThreshold: 0.001,
  }), [alignmentConstraints]);

  // Conditional returns AFTER all hooks
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={graphHeight}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!graphData || graphData.nodes.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No ownership data available
      </Alert>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 2 }} id="graph-container">
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} mb={2} spacing={{ xs: 1, md: 0 }}>
        <Typography variant="h6">
          Ownership Structure
          {companyId && ' (Focused View)'}
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            size="small"
            placeholder="Search nodes..."
            value={search}
            onChange={(e) => {
              const v = e.target.value;
              setSearch(v);
            }}
            sx={{ width: { xs: '100%', md: 'auto' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel control={<Checkbox size="small" checked={showCompanies} onChange={(e) => setShowCompanies(e.target.checked)} />} label="Companies" />
          <FormControlLabel control={<Checkbox size="small" checked={showPersons} onChange={(e) => setShowPersons(e.target.checked)} />} label="Persons" />
          <FormControlLabel control={<Checkbox size="small" checked={hideNonMatches} onChange={(e) => setHideNonMatches(e.target.checked)} />} label="Hide non-matches" />
          <Chip
            label={`${graphData.stats.companies} Companies`}
            color="primary"
            size="small"
          />
          <Chip
            label={`${graphData.stats.persons} Persons`}
            color="warning"
            size="small"
          />
          <Chip
            label={`${graphData.stats.totalEdges} Relationships`}
            color="default"
            size="small"
          />
          <Box sx={{
            borderLeft: { xs: 0, md: 1 },
            borderColor: 'divider',
            pl: { xs: 0, md: 1 },
            ml: { xs: 0, md: 1 },
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: { xs: 0.5, md: 0 },
            '& .MuiIconButton-root': {
              p: { xs: 1.5, md: 0.5 },
            },
          }}>
            <Tooltip title="Zoom In">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleZoomIn} aria-label="Zoom in">
                <ZoomIn />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom Out">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleZoomOut} aria-label="Zoom out">
                <ZoomOut />
              </IconButton>
            </Tooltip>
            <Tooltip title="Center">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleCenter} aria-label="Center graph">
                <CenterFocusStrong />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fullscreen">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleFullscreen} aria-label="Toggle fullscreen">
                <Fullscreen />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export as PNG">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleExportPng} aria-label="Export graph as PNG">
                <ImageIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Stack>

      {/* Advanced Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} mb={2}>
        {/* k-hop and min degree filters hidden as requested */}
        <Select
          multiple
          size="small"
          displayEmpty
          value={selectedJurisdictions}
          onChange={(e) => setSelectedJurisdictions(e.target.value as string[])}
          input={<OutlinedInput placeholder="Filter by Jurisdictions" />}
          renderValue={(selected) => (selected.length === 0 ? 'Filter by Jurisdictions' : `${(selected as string[]).length} selected`)}
          sx={{ minWidth: { xs: '100%', md: 220 } }}
        >
          {jurisdictions.map((j) => (
            <MenuItem key={j} value={j}>{j}</MenuItem>
          ))}
        </Select>
      </Stack>

      {/* Ownership Level Legend */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: 'text.secondary' }}>
          Ownership Level Legend (by edge color & thickness):
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 40, height: 4, bgcolor: '#d32f2f', borderRadius: 1 }} />
            <Typography variant="caption"><strong>Majority</strong> (≥50%)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 40, height: 3, bgcolor: '#f57c00', borderRadius: 1 }} />
            <Typography variant="caption"><strong>Significant</strong> (25-49%)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 40, height: 2.5, bgcolor: '#1976d2', borderRadius: 1 }} />
            <Typography variant="caption"><strong>Moderate</strong> (10-24%)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 40, height: 2, bgcolor: '#388e3c', borderRadius: 1 }} />
            <Typography variant="caption"><strong>Minor</strong> (&lt;10%)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 40, height: 1.5, bgcolor: '#9e9e9e', borderRadius: 1, border: '1px dashed #757575' }} />
            <Typography variant="caption"><strong>Unknown</strong> (no %)</Typography>
          </Box>
        </Stack>
      </Paper>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, height: graphHeight }}>
        <CytoscapeComponent
          key={`graph-${graphId}`}
          elements={elements}
          style={{ width: '100%', height: '100%' }}
          stylesheet={stylesheet}
          layout={layout}
          autoungrabify={false}
          autounselectify={false}
          boxSelectionEnabled={false}
          cy={(cy: any) => {
            cyRef.current = cy;

            // Track when initial layout completes
            cy.on('layoutstop', () => {
              if (!layoutDoneRef.current) {
                layoutDoneRef.current = true;
              }

              try {
                // Remove existing compound nodes if any
                cy.nodes('[?isCompound]').remove();

                // Group nodes by jurisdiction and create visual bounds
                Object.entries(jurisdictionGroups).forEach(([jurisdiction, nodeIds]) => {
                  if (nodeIds.length > 1) {
                    const nodes = cy.collection();
                    nodeIds.forEach(id => {
                      const node = cy.getElementById(id);
                      if (node.length > 0) nodes.merge(node);
                    });

                    if (nodes.length > 0) {
                      // Calculate bounding box for this jurisdiction
                      // Add a subtle background indicator (using compound parent approach)
                      // Note: This is visual only, we're not restructuring the graph
                      // We could add custom canvas rendering here if needed
                    }
                  }
                });
              } catch (e) {
                console.error('Error adding jurisdiction backgrounds:', e);
              }
            });

            cy.on('tap', 'node', (evt: any) => {
              const node = evt.target;
              setSelectedNode(node.data());
              setSelectedEdge(null);
              // Position popover near the node
              try {
                const rp = node.renderedPosition();
                const rect = cy.container().getBoundingClientRect();
                const left = Math.round(rect.left + rp.x);
                const top = Math.round(window.scrollY + rect.top + rp.y);
                setPopupPos({ top, left });
                setPopupOpen(true);
              } catch (_) {
                setPopupOpen(true);
              }
            });
            cy.on('tap', 'edge', (evt: any) => {
              const edge = evt.target;
              const edgeData = edge.data();
              const sourceNode = cy.getElementById(edgeData.source);
              const targetNode = cy.getElementById(edgeData.target);
              setSelectedEdge({
                ...edgeData,
                sourceName: sourceNode.data('label'),
                targetName: targetNode.data('label'),
              });
              setSelectedNode(null);
              // Position popover near the edge
              try {
                const rp = edge.renderedMidpoint();
                const rect = cy.container().getBoundingClientRect();
                const left = Math.round(rect.left + rp.x);
                const top = Math.round(window.scrollY + rect.top + rp.y);
                setPopupPos({ top, left });
                setPopupOpen(true);
              } catch (_) {
                setPopupOpen(true);
              }
            });
            cy.on('tap', (evt: any) => {
              if (evt.target === cy) {
                setPopupOpen(false);
                setSelectedNode(null);
                setSelectedEdge(null);
              }
            });
          }}
        />
      </Box>

      {/* Search results (top matches) */}
      {matchResults.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
          {matchResults.slice(0, 10).map((n: any) => (
            <Chip
              key={n.id}
              size="small"
              color={n.type === 'company' ? 'primary' : 'warning'}
              label={n.label}
              onClick={() => {
                try {
                  const cy = cyRef.current;
                  const node = cy.getElementById(n.id);
                  if (node) {
                    cy.center(node);
                    node.select();
                  }
                } catch {}
              }}
            />
          ))}
        </Stack>
      )}

      <Popover
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        anchorReference="anchorPosition"
        anchorPosition={popupPos || { top: 200, left: 200 }}
        disableRestoreFocus
      >
        {selectedNode && (
          <Box sx={{ p: 2, maxWidth: { xs: 'calc(100vw - 32px)', md: 320 }, maxHeight: { xs: '70vh', md: 'none' }, overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
              {selectedNode.type === 'company' ? 'Company' : 'Person'}
            </Typography>
            <Typography variant="body2"><strong>Name:</strong> {selectedNode.label}</Typography>
            {selectedNode.jurisdiction && (
              <Typography variant="body2"><strong>Jurisdiction:</strong> {selectedNode.jurisdiction}</Typography>
            )}
            {selectedNode.registrationNumber && (
              <Typography variant="body2"><strong>Registration #:</strong> {selectedNode.registrationNumber}</Typography>
            )}
            {selectedNode.status && (
              <Typography variant="body2"><strong>Status:</strong> {selectedNode.status}</Typography>
            )}
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={1}>
              {selectedNode.type === 'company' && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    const id = encodeURIComponent(selectedNode.id);
                    navigate(`/companies/${id}`);
                  }}
                >
                  View Company
                </Button>
              )}
              {selectedNode.type === 'person' && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    const rawId = String(selectedNode.id).startsWith('person-')
                      ? String(selectedNode.id).slice(7)
                      : String(selectedNode.id);
                    navigate(`/persons/${encodeURIComponent(rawId)}`);
                  }}
                >
                  View Person
                </Button>
              )}
              <Button size="small" onClick={() => setPopupOpen(false)}>Close</Button>
            </Stack>
          </Box>
        )}
        {selectedEdge && (
          <Box sx={{ p: 2.5, maxWidth: { xs: 'calc(100vw - 32px)', md: 380 }, maxHeight: { xs: '70vh', md: 'none' }, overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
              Ownership Relationship
            </Typography>
            
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Owner
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedEdge.sourceName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                Owned Company
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedEdge.targetName}
              </Typography>
            </Box>

            {selectedEdge.label && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Shareholding
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    label={selectedEdge.label}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      height: 32,
                      bgcolor: selectedEdge.ownershipLevel === 'majority' ? '#d32f2f' :
                               selectedEdge.ownershipLevel === 'significant' ? '#f57c00' :
                               selectedEdge.ownershipLevel === 'moderate' ? '#1976d2' :
                               selectedEdge.ownershipLevel === 'minor' ? '#388e3c' : '#9e9e9e',
                      color: 'white',
                    }}
                  />
                  {selectedEdge.percentage !== null && (
                    <Chip
                      label={
                        selectedEdge.ownershipLevel === 'majority' ? 'Majority Control' :
                        selectedEdge.ownershipLevel === 'significant' ? 'Significant Stake' :
                        selectedEdge.ownershipLevel === 'moderate' ? 'Moderate Stake' :
                        selectedEdge.ownershipLevel === 'minor' ? 'Minor Stake' : 'Unknown'
                      }
                      size="small"
                      sx={{
                        borderColor: selectedEdge.ownershipLevel === 'majority' ? '#d32f2f' :
                                     selectedEdge.ownershipLevel === 'significant' ? '#f57c00' :
                                     selectedEdge.ownershipLevel === 'moderate' ? '#1976d2' :
                                     selectedEdge.ownershipLevel === 'minor' ? '#388e3c' : '#9e9e9e',
                        color: selectedEdge.ownershipLevel === 'majority' ? '#d32f2f' :
                               selectedEdge.ownershipLevel === 'significant' ? '#f57c00' :
                               selectedEdge.ownershipLevel === 'moderate' ? '#1976d2' :
                               selectedEdge.ownershipLevel === 'minor' ? '#388e3c' : '#9e9e9e',
                        bgcolor: selectedEdge.ownershipLevel === 'majority' ? 'rgba(211, 47, 47, 0.08)' :
                                 selectedEdge.ownershipLevel === 'significant' ? 'rgba(245, 124, 0, 0.08)' :
                                 selectedEdge.ownershipLevel === 'moderate' ? 'rgba(25, 118, 210, 0.08)' :
                                 selectedEdge.ownershipLevel === 'minor' ? 'rgba(56, 142, 60, 0.08)' : 'rgba(158, 158, 158, 0.08)',
                      }}
                    />
                  )}
                </Stack>
                {selectedEdge.sharesHeld && (
                  <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                    <strong>Shares:</strong> {Number(selectedEdge.sharesHeld).toLocaleString()} shares
                  </Typography>
                )}
              </Box>
            )}

            {selectedEdge.ownershipType && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Type
                </Typography>
                <Chip
                  label={selectedEdge.ownershipType}
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setPopupOpen(false)}>Close</Button>
            </Stack>
          </Box>
        )}
      </Popover>

      {selectedNode && (
        <Paper elevation={1} sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected: {selectedNode.type === 'company' ? 'Company' : 'Person'}
          </Typography>
          <Typography variant="body2">
            <strong>Name:</strong> {selectedNode.label}
          </Typography>
          {selectedNode.jurisdiction && (
            <Typography variant="body2">
              <strong>Jurisdiction:</strong> {selectedNode.jurisdiction}
            </Typography>
          )}
          {selectedNode.registrationNumber && (
            <Typography variant="body2">
              <strong>Registration #:</strong> {selectedNode.registrationNumber}
            </Typography>
          )}
          {selectedNode.status && (
            <Typography variant="body2">
              <strong>Status:</strong> {selectedNode.status}
            </Typography>
          )}
        </Paper>
      )}
      {selectedEdge && (
        <Paper
          elevation={2}
          sx={{
            mt: 2,
            p: 2.5,
            bgcolor: selectedEdge.ownershipLevel === 'majority' ? 'rgba(211, 47, 47, 0.05)' :
                     selectedEdge.ownershipLevel === 'significant' ? 'rgba(245, 124, 0, 0.05)' :
                     selectedEdge.ownershipLevel === 'moderate' ? 'rgba(25, 118, 210, 0.05)' :
                     selectedEdge.ownershipLevel === 'minor' ? 'rgba(56, 142, 60, 0.05)' : 'grey.50',
            borderLeft: 4,
            borderColor: selectedEdge.ownershipLevel === 'majority' ? '#d32f2f' :
                         selectedEdge.ownershipLevel === 'significant' ? '#f57c00' :
                         selectedEdge.ownershipLevel === 'moderate' ? '#1976d2' :
                         selectedEdge.ownershipLevel === 'minor' ? '#388e3c' : '#9e9e9e',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Selected Ownership:
            </Typography>
            <Chip
              label={selectedEdge.label}
              size="small"
              sx={{
                bgcolor: selectedEdge.ownershipLevel === 'majority' ? '#d32f2f' :
                         selectedEdge.ownershipLevel === 'significant' ? '#f57c00' :
                         selectedEdge.ownershipLevel === 'moderate' ? '#1976d2' :
                         selectedEdge.ownershipLevel === 'minor' ? '#388e3c' : '#9e9e9e',
                color: 'white',
                fontWeight: 'bold',
              }}
            />
            {selectedEdge.percentage !== null && (
              <Chip
                label={
                  selectedEdge.ownershipLevel === 'majority' ? 'Majority Control' :
                  selectedEdge.ownershipLevel === 'significant' ? 'Significant' :
                  selectedEdge.ownershipLevel === 'moderate' ? 'Moderate' :
                  'Minor'
                }
                size="small"
                variant="outlined"
                sx={{
                  borderColor: selectedEdge.ownershipLevel === 'majority' ? '#d32f2f' :
                               selectedEdge.ownershipLevel === 'significant' ? '#f57c00' :
                               selectedEdge.ownershipLevel === 'moderate' ? '#1976d2' :
                               selectedEdge.ownershipLevel === 'minor' ? '#388e3c' : '#9e9e9e',
                  color: selectedEdge.ownershipLevel === 'majority' ? '#d32f2f' :
                         selectedEdge.ownershipLevel === 'significant' ? '#f57c00' :
                         selectedEdge.ownershipLevel === 'moderate' ? '#1976d2' :
                         selectedEdge.ownershipLevel === 'minor' ? '#388e3c' : '#9e9e9e',
                  fontWeight: 600,
                }}
              />
            )}
          </Stack>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Owner:</strong> {selectedEdge.sourceName}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Owned:</strong> {selectedEdge.targetName}
          </Typography>
          {selectedEdge.sharesHeld && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {Number(selectedEdge.sharesHeld).toLocaleString()} shares
              {selectedEdge.ownershipType && ` • ${selectedEdge.ownershipType}`}
            </Typography>
          )}
        </Paper>
      )}
    </Paper>
  );
};

export default OwnershipGraph;
